import { Button, Icon, type ToastType, Tooltip } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { packages, type PackageWithFeatureCodes } from "../../api/packages.service";
import { editorialCategories as editorialCategoriesService } from "../../api/editorialCategories.service";
import { PackageDeleteModal } from "../../components/PackageDeleteModal";
import PackageDetailLoader from "../../components/PackageDetailLoader";
import { PackageForm } from "../../components/PackageForm";
import { PackageVersionModal } from "../../components/PackageVersionModal";
import { PackageVersionHistory } from "../../components/PackageVersionHistory";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import EmptyState from "../../components/EmptyState";
import type { PackageFormData } from "../../schemas/packageSchema";
import { SCOPE_NAMES } from "../../constants/scopes";
import styles from "./PackageDetail.module.css";

type PackageDetailContext = {
  toast: { message: string; type: ToastType } | null;
  setToast: (toast: { message: string; type: ToastType } | null) => void;
  onMutationSuccess?: () => void;
  setIsEditMode?: (isEditMode: boolean) => void;
};

const FIELD_LABELS: Record<string, string> = {
  contact_ids: "Contacts",
  office_ids: "Offices",
};

function getFieldLabel(field: string): string {
  return FIELD_LABELS[field] || field;
}

function PackageDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    setToast,
    onMutationSuccess,
    setIsEditMode: setParentEditMode,
  } = useOutletContext<PackageDetailContext>();
  const [pkg, setPkg] = useState<PackageWithFeatureCodes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [activePackage, setActivePackage] = useState<PackageWithFeatureCodes | null>(null);
  const [loadingActivePackage, setLoadingActivePackage] = useState(false);
  const [defaultRegionName, setDefaultRegionName] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadPackage(id);
    }
  }, [id]);

  useEffect(() => {
    if (pkg && !pkg.isActive) {
      loadActivePackage(pkg.productCode);
    }
  }, [pkg]);

  useEffect(() => {
    const defaultSiteStructureId = pkg?.defaultSiteStructureId;
    if (!defaultSiteStructureId) {
      setDefaultRegionName(null);
      return;
    }
    if (defaultSiteStructureId in SCOPE_NAMES) {
      setDefaultRegionName(SCOPE_NAMES[defaultSiteStructureId]);
      return;
    }
    editorialCategoriesService
      .getAll({ id: Number(defaultSiteStructureId), limit: 1 })
      .then(response => {
        const category = response.editorialCategories[0];
        setDefaultRegionName(category?.country ?? null);
      })
      .catch((err: unknown) => {
        console.error("Failed to load default region name", err);
        setDefaultRegionName(null);
      });
  }, [pkg?.defaultSiteStructureId]);

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (setParentEditMode) {
      setParentEditMode(isEditMode);
    }
  }, [isEditMode, setParentEditMode]);

  useEffect(() => {
    return () => {
      if (setParentEditMode) {
        setParentEditMode(false);
      }
    };
  }, [setParentEditMode]);

  async function loadPackage(packageId: string) {
    try {
      setLoading(true);
      setError(null);
      const packageData = await packages.getById(packageId);
      setPkg(packageData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load package"));
    } finally {
      setLoading(false);
    }
  }

  async function loadActivePackage(productCode: string) {
    try {
      setLoadingActivePackage(true);
      const versions = await packages.getVersionsByProductCode(productCode);
      const active = versions.find(v => v.isActive);
      setActivePackage(active || null);
    } catch (err) {
      console.error("Failed to load active package:", err);
    } finally {
      setLoadingActivePackage(false);
    }
  }

  function handleGoToLatestVersion() {
    if (activePackage) {
      navigate(`/fulfilment/packages/${activePackage.id}`);
    }
  }

  function handleEditClick() {
    setIsEditMode(true);
  }

  function handleCancelEdit() {
    if (isDirty) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to discard them?",
      );
      if (!confirmDiscard) {
        return;
      }
    }
    setIsEditMode(false);
    setIsDirty(false);
  }

  async function handleSave(data: PackageFormData) {
    if (!id) return;

    setIsSubmitting(true);
    try {
      const updatedPackage = await packages.update(id, {
        name: data.name,
        description: data.description || null,
        featureCodes: data.featureCodes,
        metadataQuantities: data.metadataQuantities,
        isActive: data.isActive,
        defaultSiteStructureId: data.defaultSiteStructureId ?? null,
      });
      setPkg(updatedPackage);
      setIsEditMode(false);
      setIsDirty(false);
      setToast({
        message: `Package "${updatedPackage.productCode}" updated successfully`,
        type: "success",
      });
      onMutationSuccess?.();
    } catch (err) {
      setToast({
        message: `Failed to update package: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDeleteClick() {
    setIsDeleteModalOpen(true);
  }

  function handleCloseDeleteModal() {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!pkg) return;

    setIsDeleting(true);
    try {
      await packages.delete(pkg.id);
      setIsDeleteModalOpen(false);
      setToast({
        message: `Package "${pkg.productCode}" deleted successfully`,
        type: "success",
      });
      onMutationSuccess?.();
      navigate("/fulfilment/packages");
    } catch (err) {
      setToast({
        message: `Failed to delete package: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  }

  function handleDirtyChange(dirty: boolean) {
    setIsDirty(dirty);
  }

  if (loading) {
    return <PackageDetailLoader />;
  }

  if (error || !pkg) {
    // Check if this is a 401 Unauthorized or 403 Forbidden error
    const status = error && (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;

    if (isAuthError) {
      return <UnauthorizedView />;
    }

    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Failed to load package"
          message={error?.message || "Package not found"}
          action={{
            label: "Back to Packages",
            onClick: () => navigate("/fulfilment/packages"),
          }}
        />
      </div>
    );
  }

  const packageType =
    pkg.type === "default" ? "DEFAULT" : pkg.type === "product" ? "PRODUCT" : "PRODUCT";

  if (isEditMode) {
    return (
      <>
        <div className={styles.header}>
          <h1 className={styles.title}>Edit Package</h1>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleCancelEdit}
            aria-label="Back to package detail"
          >
            <Icon icon="arrow-left" size="small" />
            <span>Back</span>
          </button>
        </div>

        <div className={styles.formContainer}>
          <PackageForm
            mode="edit"
            initialValues={pkg}
            onSubmit={handleSave}
            onCancel={handleCancelEdit}
            submitLabel="Save Changes"
            isSubmitting={isSubmitting}
            onDirtyChange={handleDirtyChange}
            featurePickerColumns={{ mobile: 1, tablet: 2, desktop: 2 }}
            featurePickerCompact={true}
            featurePickerAutoFit={true}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1 className={styles.packageTitle}>{pkg.name}</h1>
          <span className={styles.versionBadge}>v{pkg.version}</span>
          {!pkg.isActive && <span className={styles.inactiveBadge}>Inactive</span>}
          <span className={styles.typeBadge} data-type={packageType.toLowerCase()}>
            {packageType.charAt(0).toUpperCase() + packageType.slice(1).toLowerCase()}
          </span>
        </div>
        <div className={styles.headerActions}>
          {pkg.isActive ? (
            <Tooltip
              title=""
              content="Cannot delete active version. Activate a different version first."
              position="top"
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteClick}
                disabled={pkg.isActive}
              >
                Delete
              </Button>
            </Tooltip>
          ) : (
            <Button variant="outlined" color="error" onClick={handleDeleteClick}>
              Delete
            </Button>
          )}
          {pkg.isActive ? (
            <>
              <Button variant="outlined" color="primary" onClick={() => setShowVersionModal(true)}>
                Create Version {pkg.version + 1}
              </Button>
              <Button variant="contained" color="primary" onClick={handleEditClick}>
                Edit
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleGoToLatestVersion}
              disabled={loadingActivePackage || !activePackage}
            >
              {loadingActivePackage ? "Loading..." : "Go to Latest Version"}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Package Information</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Product Code</span>
              <span className={styles.infoValue}>{pkg.productCode}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Version</span>
              <span className={styles.infoValue}>{pkg.version}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Status</span>
              <span className={styles.statusBadge} data-active={pkg.isActive}>
                {pkg.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Description</span>
              <span className={styles.infoValue}>
                {pkg.description || <span className={styles.emptyText}>No description</span>}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Default Region</span>
              <span className={styles.infoValue}>
                {defaultRegionName || <span className={styles.emptyText}>None</span>}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Features ({pkg.featureCodes?.length ?? 0})</h2>
          {pkg?.featureCodes?.length && pkg.featureCodes.length > 0 ? (
            <div className={styles.featureList}>
              {pkg?.featureCodes?.map(code => (
                <span key={code} className={styles.featureChip}>
                  {code}
                </span>
              ))}
            </div>
          ) : (
            <p className={styles.emptyText}>No features assigned</p>
          )}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Metadata Configuration</h2>
          {pkg.metadataQuantities && Object.keys(pkg.metadataQuantities).length > 0 ? (
            <div className={styles.infoGrid}>
              {Object.entries(pkg.metadataQuantities).map(([field, quantity]) => (
                <div key={field} className={styles.infoItem}>
                  <span className={styles.infoLabel}>{getFieldLabel(field)}</span>
                  <span className={styles.infoValue}>{quantity}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.emptyText}>Not configured</p>
          )}
        </div>

        <PackageVersionHistory productCode={pkg.productCode} currentVersionId={pkg.id} />
      </div>

      <PackageDeleteModal
        package={pkg}
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      <PackageVersionModal
        package={pkg}
        isOpen={showVersionModal}
        onClose={() => setShowVersionModal(false)}
        setToast={setToast}
      />
    </>
  );
}

export default PackageDetail;
